import { ActionIcon, Badge, Box, Center, Divider, Group, Loader, Popover, ScrollArea, Stack, Text, Tooltip } from '@mantine/core';
import { IconBell, IconTool, type Icon } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { INotificacion, TipoNotificacion } from './interfaces';

interface CampanaNotificacionesVistaProps {
    noLeidas: number;
    notificaciones: INotificacion[];
    abierta: boolean;
    cargando: boolean;
    alAlternar: (abrir: boolean) => void;
}

/** Mismo rojo del botón de emergencia de la barra. */
const COLOR_ALERTA = '#d7263d';

/** Ícono por tipo de notificación. */
const ICONOS: Record<TipoNotificacion, Icon> = {
    mantenimientoACosto: IconTool
};

export const CampanaNotificacionesVista = ({ noLeidas, notificaciones, abierta, cargando, alAlternar }: CampanaNotificacionesVistaProps) => {
    // Al abrir se marcan como vistas (el contador queda en 0); en la lista abierta siguen resaltadas las nuevas.
    const nuevas = notificaciones.filter(n => !n.Leida).length;
    return (
        <Popover opened={abierta} onChange={alAlternar} position="bottom-end" width={340} shadow="md" withinPortal zIndex={9999}>
            <Popover.Target>
                <Tooltip label="Notificaciones" withinPortal zIndex={9999} disabled={abierta}>
                    {/* Contador propio: el Indicator de Mantine ignora el color sobre la barra. Borde del azul de la barra. */}
                    <Box pos="relative" style={{ display: 'inline-flex' }}>
                        <ActionIcon variant="subtle" color="white" radius="xl" size="lg" onClick={() => alAlternar(!abierta)}
                            aria-label="Notificaciones">
                            <IconBell size={20} />
                        </ActionIcon>
                        {noLeidas > 0 && (
                            <Box component="span" style={{
                                position: 'absolute', top: -2, right: -4, minWidth: 18, height: 18, padding: '0 4px',
                                borderRadius: 9, backgroundColor: COLOR_ALERTA, color: '#fff', fontSize: 10, fontWeight: 700,
                                lineHeight: '14px', textAlign: 'center', border: '2px solid var(--mantine-color-primary-0)',
                                pointerEvents: 'none', boxSizing: 'border-box'
                            }}>
                                {noLeidas > 99 ? '99+' : noLeidas}
                            </Box>
                        )}
                    </Box>
                </Tooltip>
            </Popover.Target>

            <Popover.Dropdown p={0}>
                <Group justify="space-between" px="md" py="sm">
                    <Text fw={600} fz={13}>Notificaciones</Text>
                    {nuevas > 0 && (
                        <Badge variant="light" size="sm" styles={{ root: { backgroundColor: '#fdecee', color: COLOR_ALERTA } }}>
                            {nuevas} {nuevas === 1 ? 'nueva' : 'nuevas'}
                        </Badge>
                    )}
                </Group>
                <Divider />
                {cargando ? (
                    <Center py="lg"><Loader size="sm" /></Center>
                ) : notificaciones.length === 0 ? (
                    <Text fz={12} c="dimmed" ta="center" py="lg">No tienes notificaciones.</Text>
                ) : (
                    <ScrollArea.Autosize mah={400}>
                        <Stack gap={0}>
                            {notificaciones.map(notificacion => {
                                const IconoTipo = ICONOS[notificacion.Tipo];
                                return (
                                    <Box key={notificacion.Clave} px="sm" py={8} style={{
                                        borderBottom: '1px solid var(--mantine-color-gray-2)',
                                        backgroundColor: notificacion.Leida ? undefined : 'var(--mantine-color-primary-light)'
                                    }}>
                                        <Group gap="sm" wrap="nowrap" align="flex-start">
                                            <IconoTipo size={16} style={{ marginTop: 2, flexShrink: 0 }} color="var(--mantine-color-primary-filled)" />
                                            <Box style={{ flex: 1, minWidth: 0 }}>
                                                <Group justify="space-between" gap="xs" wrap="nowrap">
                                                    <Text fz={10} fw={700} c="dimmed" tt="uppercase">{notificacion.Titulo}</Text>
                                                    <Text fz={10} c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                                                        {dayjs(notificacion.Fecha).format('DD/MM/YYYY')}
                                                    </Text>
                                                </Group>
                                                <Text fz={12} lh={1.35} fw={notificacion.Leida ? 400 : 600}>{notificacion.Mensaje}</Text>
                                            </Box>
                                            {!notificacion.Leida && (
                                                <Box w={8} h={8} mt={6} style={{ borderRadius: '50%', backgroundColor: COLOR_ALERTA, flexShrink: 0 }} />
                                            )}
                                        </Group>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </ScrollArea.Autosize>
                )}
            </Popover.Dropdown>
        </Popover>
    );
};
